import { useEffect } from 'react';

/**
 * iOS Safari fix for bottom sheets rendered inside AdvancedPullToRefresh (PTR).
 *
 * PTR attaches a native non-passive `touchmove` listener on its container and
 * calls preventDefault() on any downward finger movement, which blocks the
 * "scroll up" gesture inside an open sheet on iOS Safari. Native listeners on
 * the sheet overlay fire first (inner element bubbles first), so
 * stopPropagation isolates the sheet from PTR.
 *
 * iOS quirk: if the inner scroller is exactly at a boundary when the touch
 * starts, Safari hands the gesture to the page instead of the scroller.
 * Nudging 1px off the boundary makes the scroller always win.
 *
 * Usage:
 *   const overlayRef = useRef(null);
 *   useSheetTouchIsolation(overlayRef, show);
 *   ...
 *   <div ref={overlayRef} className="bottom-sheet-overlay ...">
 *       ...
 *       <div className="scrollable-content">...</div>
 *   </div>
 */
const useSheetTouchIsolation = (overlayRef, active, scrollableSelector = '.scrollable-content') => {
    useEffect(() => {
        if (!active) return undefined;
        const overlay = overlayRef.current;
        if (!overlay) return undefined;

        const stop = (e) => e.stopPropagation();

        const nudge = () => {
            const sc = overlay.querySelector(scrollableSelector);
            if (!sc) return;
            if (sc.scrollTop <= 0) {
                sc.scrollTop = 1;
            } else if (sc.scrollTop + sc.clientHeight >= sc.scrollHeight) {
                sc.scrollTop = sc.scrollHeight - sc.clientHeight - 1;
            }
        };

        const onTouchStart = (e) => {
            stop(e);
            nudge();
        };

        overlay.addEventListener('touchstart', onTouchStart, { passive: true });
        overlay.addEventListener('touchmove', stop, { passive: true });
        overlay.addEventListener('touchend', stop, { passive: true });
        overlay.addEventListener('touchcancel', stop, { passive: true });

        return () => {
            overlay.removeEventListener('touchstart', onTouchStart);
            overlay.removeEventListener('touchmove', stop);
            overlay.removeEventListener('touchend', stop);
            overlay.removeEventListener('touchcancel', stop);
        };
    }, [overlayRef, active, scrollableSelector]);
};

export default useSheetTouchIsolation;
